// Documentation page JavaScript

import {
    apiRequest,
    initAuthenticatedPage,
    renderMarkdown,
    getDefaultProjectId,
    setDefaultProjectId,
    copyToClipboard,
} from "./common";

// Types
interface Project {
    id: number;
    name: string;
    key: string;
}

interface Tag {
    id: number;
    project_id: number;
    name: string;
    created_at: string;
}

interface DocumentationPage {
    id: number;
    project_id: number;
    title: string;
    content: string;
    description: string | null;
    parent_id: number | null;
    position: number;
    created_at: string;
    updated_at: string;
    tags: Tag[];
}

interface DocumentationTreeNode {
    page: DocumentationPage;
    children: DocumentationTreeNode[];
}

// State
let currentProjectId: number | null = null;
let currentPageId: number | null = null;
let currentPage: DocumentationPage | null = null;
let docTree: DocumentationTreeNode[] = [];
let projectTags: Tag[] = [];
const expandedNodes: Set<number> = new Set();
let activeContextMenu: HTMLElement | null = null;

// DOM elements
let projectSelect: HTMLSelectElement;
let docsTree: HTMLElement;
let docsViewer: HTMLElement;
let newPageBtn: HTMLButtonElement;
let searchBtn: HTMLButtonElement;
let manageTagsBtn: HTMLButtonElement;

// Delete dialog elements
let deletePageDialog: HTMLDialogElement;
let deletePageName: HTMLElement;
let deletePageConfirmBtn: HTMLButtonElement;

// Tags dialog elements
let tagsDialog: HTMLDialogElement;
let newTagNameInput: HTMLInputElement;
let addTagBtn: HTMLButtonElement;
let tagsList: HTMLElement;

// Search dialog elements
let searchDialog: HTMLDialogElement;
let searchTagsList: HTMLElement;
let searchExecuteBtn: HTMLButtonElement;
let searchResults: HTMLElement;
let searchResultsList: HTMLElement;

function escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

// Initialize
async function initDocsPage(): Promise<void> {
    // Wait for authentication to complete before making API requests
    const user = await initAuthenticatedPage();
    if (!user) {
        return;
    }

    // Get DOM elements
    projectSelect = document.getElementById(
        "project-select"
    ) as HTMLSelectElement;
    docsTree = document.getElementById("docs-tree") as HTMLElement;
    docsViewer = document.getElementById("docs-viewer") as HTMLElement;
    newPageBtn = document.getElementById("new-page-btn") as HTMLButtonElement;
    searchBtn = document.getElementById("search-btn") as HTMLButtonElement;
    manageTagsBtn = document.getElementById(
        "manage-tags-btn"
    ) as HTMLButtonElement;

    // Delete dialog
    deletePageDialog = document.getElementById(
        "delete-page-dialog"
    ) as HTMLDialogElement;
    deletePageName = document.getElementById("delete-page-name") as HTMLElement;
    deletePageConfirmBtn = document.getElementById(
        "delete-page-confirm-btn"
    ) as HTMLButtonElement;

    // Tags dialog
    tagsDialog = document.getElementById("tags-dialog") as HTMLDialogElement;
    newTagNameInput = document.getElementById(
        "new-tag-name"
    ) as HTMLInputElement;
    addTagBtn = document.getElementById("add-tag-btn") as HTMLButtonElement;
    tagsList = document.getElementById("tags-list") as HTMLElement;

    // Search dialog
    searchDialog = document.getElementById(
        "search-dialog"
    ) as HTMLDialogElement;
    searchTagsList = document.getElementById("search-tags-list") as HTMLElement;
    searchExecuteBtn = document.getElementById(
        "search-execute-btn"
    ) as HTMLButtonElement;
    searchResults = document.getElementById("search-results") as HTMLElement;
    searchResultsList = document.getElementById(
        "search-results-list"
    ) as HTMLElement;

    // Setup event listeners
    setupEventListeners();

    // Check for projectId in URL params (returning from doc-edit)
    const params = new URLSearchParams(window.location.search);
    const urlProjectId = params.get("projectId");

    // Load projects, then select if specified (URL param takes precedence over default)
    loadProjects().then(() => {
        const projectIdToSelect =
            urlProjectId || getDefaultProjectId()?.toString();
        if (projectIdToSelect) {
            // Verify the project exists in the dropdown before selecting
            const optionExists = Array.from(projectSelect.options).some(
                (opt) => opt.value === projectIdToSelect
            );
            if (optionExists) {
                projectSelect.value = projectIdToSelect;
                onProjectChange();
            }
        }
    });
}

function setupEventListeners(): void {
    // Project selection
    projectSelect.addEventListener("change", onProjectChange);

    // Header buttons
    newPageBtn.addEventListener("click", () => navigateToEditor(null, null));
    searchBtn.addEventListener("click", openSearchDialog);
    manageTagsBtn.addEventListener("click", openTagsDialog);

    // Delete dialog
    document
        .getElementById("delete-page-cancel-btn")
        ?.addEventListener("click", closeDeleteDialog);
    deletePageConfirmBtn.addEventListener("click", deletePage);
    deletePageDialog.addEventListener("click", (e) => {
        if (e.target === deletePageDialog) closeDeleteDialog();
    });

    // Tags dialog
    document
        .getElementById("tags-dialog-close-btn")
        ?.addEventListener("click", closeTagsDialog);
    document
        .getElementById("tags-dialog-done-btn")
        ?.addEventListener("click", closeTagsDialog);
    addTagBtn.addEventListener("click", addTag);
    newTagNameInput.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addTag();
        }
    });
    tagsDialog.addEventListener("click", (e) => {
        if (e.target === tagsDialog) closeTagsDialog();
    });

    // Search dialog
    document
        .getElementById("search-dialog-close-btn")
        ?.addEventListener("click", closeSearchDialog);
    document
        .getElementById("search-dialog-close-footer-btn")
        ?.addEventListener("click", closeSearchDialog);
    searchExecuteBtn.addEventListener("click", executeSearch);
    searchDialog.addEventListener("click", (e) => {
        if (e.target === searchDialog) closeSearchDialog();
    });

    // Close context menu when clicking elsewhere
    document.addEventListener("click", (e) => {
        if (
            activeContextMenu &&
            !activeContextMenu.contains(e.target as Node)
        ) {
            closeContextMenu();
        }
    });

    // Keyboard shortcuts
    document.addEventListener("keydown", handleKeydown);
}

// Navigation to editor page
function navigateToEditor(
    pageId: number | null,
    parentId: number | null
): void {
    if (!currentProjectId) return;

    const params = new URLSearchParams();
    params.set("projectId", currentProjectId.toString());
    if (pageId !== null) {
        params.set("pageId", pageId.toString());
    }
    if (parentId !== null) {
        params.set("parentId", parentId.toString());
    }
    window.location.href = `/pages/doc-edit.html?${params.toString()}`;
}

// Project loading and selection
async function loadProjects(): Promise<void> {
    try {
        const projects = await apiRequest<Project[]>("/projects");
        renderProjectOptions(projects);
    } catch (error) {
        console.error("Failed to load projects:", error);
    }
}

function renderProjectOptions(projects: Project[]): void {
    const options = projects.map(
        (p) =>
            `<option value="${p.id}">${escapeHtml(p.name)} (${escapeHtml(p.key)})</option>`
    );
    projectSelect.innerHTML = `<option value="">Select a project...</option>${options.join("")}`;
}

async function onProjectChange(): Promise<void> {
    const projectId = projectSelect.value
        ? parseInt(projectSelect.value, 10)
        : null;
    currentProjectId = projectId;
    currentPageId = null;
    currentPage = null;

    // Clear the content viewer when project changes
    docsViewer.innerHTML =
        '<div class="empty-state"><p>Select a page from the tree to view its content</p></div>';

    // Save as default project
    setDefaultProjectId(projectId);

    // Enable/disable buttons
    const hasProject = projectId !== null;
    newPageBtn.disabled = !hasProject;
    searchBtn.disabled = !hasProject;
    manageTagsBtn.disabled = !hasProject;

    if (hasProject) {
        await Promise.all([loadDocTree(), loadTags()]);
    } else {
        docsTree.innerHTML =
            '<p class="muted">Select a project to view documentation</p>';
        docsViewer.innerHTML =
            '<div class="empty-state"><p>Select a page from the tree to view its content</p></div>';
        projectTags = [];
    }
}

// Documentation tree
async function loadDocTree(): Promise<void> {
    if (!currentProjectId) return;

    docsTree.innerHTML = '<p class="muted">Loading...</p>';

    try {
        docTree = await apiRequest<DocumentationTreeNode[]>(
            `/projects/${currentProjectId}/docs/tree`
        );
        renderDocTree();
    } catch (error) {
        docsTree.innerHTML = `<p class="error">Failed to load documentation: ${error instanceof Error ? error.message : "Unknown error"}</p>`;
    }
}

function renderDocTree(): void {
    if (docTree.length === 0) {
        docsTree.innerHTML =
            '<p class="muted">No documentation pages yet. Click "New Page" to create one.</p>';
        return;
    }

    docsTree.innerHTML = renderTreeNodes(docTree);
    attachTreeEventListeners();
}

function renderTreeNodes(nodes: DocumentationTreeNode[]): string {
    return nodes
        .map((node) => {
            const hasChildren = node.children.length > 0;
            const isExpanded = expandedNodes.has(node.page.id);
            const isSelected = currentPageId === node.page.id;

            const toggleIcon = hasChildren ? (isExpanded ? "▼" : "▶") : "";
            const toggleClass = hasChildren ? "has-children" : "";

            return `
            <div class="tree-node" data-page-id="${node.page.id}">
                <div class="tree-item ${isSelected ? "selected" : ""}">
                    <span class="tree-toggle ${toggleClass}" data-page-id="${node.page.id}">${toggleIcon}</span>
                    <span class="tree-title" data-page-id="${node.page.id}">${escapeHtml(node.page.title)}</span>
                    <span class="tree-actions">
                        <button type="button" class="context-menu-btn" data-page-id="${node.page.id}" title="Actions">⋮</button>
                        <div class="context-menu" data-page-id="${node.page.id}">
                            <button type="button" class="context-menu-item add-child-btn" data-page-id="${node.page.id}">
                                <span class="context-icon">+</span> Add child page
                            </button>
                            <button type="button" class="context-menu-item edit-btn" data-page-id="${node.page.id}">
                                <span class="context-icon">✎</span> Edit page
                            </button>
                            <button type="button" class="context-menu-item delete-btn" data-page-id="${node.page.id}">
                                <span class="context-icon">×</span> Delete page
                            </button>
                        </div>
                    </span>
                </div>
                ${hasChildren ? `<div class="tree-children ${isExpanded ? "" : "collapsed"}">${renderTreeNodes(node.children)}</div>` : ""}
            </div>
        `;
        })
        .join("");
}

function attachTreeEventListeners(): void {
    // Toggle expand/collapse
    docsTree.querySelectorAll(".tree-toggle.has-children").forEach((toggle) => {
        toggle.addEventListener("click", (e) => {
            e.stopPropagation();
            const pageId = parseInt(
                (e.target as HTMLElement).dataset.pageId || "0",
                10
            );
            toggleNode(pageId);
        });
    });

    // Select page - clicking anywhere on tree-item selects the page
    docsTree.querySelectorAll(".tree-item").forEach((item) => {
        item.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            // Don't select if clicking on context menu area
            if (target.closest(".tree-actions")) {
                return;
            }
            // Don't select if clicking on toggle that has children (it will expand/collapse instead)
            const toggle = target.closest(".tree-toggle");
            if (toggle && toggle.classList.contains("has-children")) {
                return;
            }
            const treeNode = (item as HTMLElement).closest(
                ".tree-node"
            ) as HTMLElement | null;
            const pageId = parseInt(treeNode?.dataset.pageId || "0", 10);
            if (pageId) {
                selectPage(pageId);
            }
        });
    });

    // Context menu toggle
    docsTree.querySelectorAll(".context-menu-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const pageId = parseInt(
                (e.target as HTMLElement).dataset.pageId || "0",
                10
            );
            toggleContextMenu(pageId, e.target as HTMLElement);
        });
    });

    // Add child page
    docsTree.querySelectorAll(".add-child-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const pageId = parseInt(
                (e.target as HTMLElement).dataset.pageId || "0",
                10
            );
            closeContextMenu();
            navigateToEditor(null, pageId);
        });
    });

    // Edit page
    docsTree.querySelectorAll(".edit-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const pageId = parseInt(
                (e.target as HTMLElement).dataset.pageId || "0",
                10
            );
            closeContextMenu();
            navigateToEditor(pageId, null);
        });
    });

    // Delete page
    docsTree.querySelectorAll(".delete-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const pageId = parseInt(
                (e.target as HTMLElement).dataset.pageId || "0",
                10
            );
            closeContextMenu();
            const page = findPageInTree(pageId);
            if (page) openDeleteDialog(page);
        });
    });
}

function toggleContextMenu(pageId: number, button: HTMLElement): void {
    const treeActions = button.closest(".tree-actions");
    const menu = treeActions?.querySelector(
        `.context-menu[data-page-id="${pageId}"]`
    ) as HTMLElement | null;

    if (!menu) return;

    if (activeContextMenu === menu) {
        closeContextMenu();
    } else {
        closeContextMenu();
        menu.classList.add("visible");
        activeContextMenu = menu;
    }
}

function closeContextMenu(): void {
    if (activeContextMenu) {
        activeContextMenu.classList.remove("visible");
        activeContextMenu = null;
    }
}

function toggleNode(pageId: number): void {
    if (expandedNodes.has(pageId)) {
        expandedNodes.delete(pageId);
    } else {
        expandedNodes.add(pageId);
    }
    renderDocTree();
}

function findPageInTree(pageId: number): DocumentationPage | null {
    function search(nodes: DocumentationTreeNode[]): DocumentationPage | null {
        for (const node of nodes) {
            if (node.page.id === pageId) return node.page;
            const found = search(node.children);
            if (found) return found;
        }
        return null;
    }
    return search(docTree);
}

// Page viewing
async function selectPage(pageId: number): Promise<void> {
    if (!currentProjectId) return;

    currentPageId = pageId;
    renderDocTree(); // Update selection highlight

    try {
        currentPage = await apiRequest<DocumentationPage>(
            `/projects/${currentProjectId}/docs/${pageId}`
        );
        renderPageViewer(currentPage);
    } catch (error) {
        docsViewer.innerHTML = `<div class="error">Failed to load page: ${error instanceof Error ? error.message : "Unknown error"}</div>`;
    }
}

function renderPageViewer(page: DocumentationPage): void {
    const descriptionHtml = page.description
        ? `<div class="page-description-section">
               <h4 class="page-description-label">Description</h4>
               <p class="page-description">${escapeHtml(page.description)}</p>
           </div>`
        : `<div class="page-description-section">
               <h4 class="page-description-label">Description</h4>
               <p class="page-description missing">No description provided</p>
           </div>`;

    const tagsHtml =
        page.tags.length > 0
            ? `<div class="page-tags">${page.tags.map((t) => `<span class="tag">${escapeHtml(t.name)}</span>`).join("")}</div>`
            : "";

    const timestampsHtml = `
        <div class="page-timestamps">
            <span class="timestamp">Created: ${formatDate(page.created_at)}</span>
            <span class="timestamp">Updated: ${formatDate(page.updated_at)}</span>
        </div>
    `;

    docsViewer.innerHTML = `
        <div class="page-viewer-header">
            <div>
                <h2>${escapeHtml(page.title)}</h2>
                ${descriptionHtml}
                ${tagsHtml}
                ${timestampsHtml}
            </div>
            <div class="page-viewer-actions">
                <button type="button" class="outline secondary icon-btn" id="viewer-copy-btn" title="Copy to clipboard">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                </button>
                <button type="button" class="outline secondary icon-btn" id="viewer-fullscreen-btn" title="Full screen (F11)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" y1="3" x2="14" y2="10"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                    </svg>
                </button>
                <button type="button" class="outline secondary" id="viewer-edit-btn">Edit</button>
                <button type="button" class="outline delete-action" id="viewer-delete-btn">Delete</button>
            </div>
        </div>
        <button type="button" class="fullscreen-close-btn" id="fullscreen-close-btn" title="Exit full screen (Esc)">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        </button>
        <div class="page-content markdown-content">${renderMarkdown(page.content || "*No content*")}</div>
    `;

    document
        .getElementById("viewer-copy-btn")
        ?.addEventListener("click", () => {
            copyPageToClipboard(page);
        });

    document
        .getElementById("viewer-fullscreen-btn")
        ?.addEventListener("click", () => {
            enterFullscreen();
        });

    document
        .getElementById("fullscreen-close-btn")
        ?.addEventListener("click", () => {
            exitFullscreen();
        });

    document
        .getElementById("viewer-edit-btn")
        ?.addEventListener("click", () => {
            navigateToEditor(page.id, null);
        });

    document
        .getElementById("viewer-delete-btn")
        ?.addEventListener("click", () => {
            openDeleteDialog(page);
        });
}

// Copy page to clipboard
function copyPageToClipboard(page: DocumentationPage): void {
    const parts: string[] = [];

    // Add title as markdown heading
    parts.push(`# ${page.title}`);
    parts.push("");

    // Add content
    if (page.content) {
        parts.push(page.content);
    }

    const text = parts.join("\n");
    copyToClipboard(text, "Page copied to clipboard");
}

// Fullscreen mode
function enterFullscreen(): void {
    const docsContent = document.querySelector(".docs-content") as HTMLElement;
    if (docsContent && docsContent.requestFullscreen) {
        docsContent.requestFullscreen();
    }
}

function exitFullscreen(): void {
    if (document.fullscreenElement) {
        document.exitFullscreen();
    }
}

function handleKeydown(e: KeyboardEvent): void {
    // F11 to toggle fullscreen (only when a page is selected)
    if (e.key === "F11" && currentPage) {
        e.preventDefault();
        if (document.fullscreenElement) {
            exitFullscreen();
        } else {
            enterFullscreen();
        }
    }
}

// Delete dialog
let pageToDelete: DocumentationPage | null = null;

function openDeleteDialog(page: DocumentationPage): void {
    pageToDelete = page;
    deletePageName.textContent = page.title;
    deletePageDialog.showModal();
}

function closeDeleteDialog(): void {
    deletePageDialog.close();
    pageToDelete = null;
}

async function deletePage(): Promise<void> {
    if (!currentProjectId || !pageToDelete) return;

    deletePageConfirmBtn.setAttribute("aria-busy", "true");
    deletePageConfirmBtn.disabled = true;

    try {
        await apiRequest(
            `/projects/${currentProjectId}/docs/${pageToDelete.id}`,
            {
                method: "DELETE",
            }
        );

        const deletedId = pageToDelete.id;
        closeDeleteDialog();
        await loadDocTree();

        // Clear viewer if we deleted the current page
        if (currentPageId === deletedId) {
            currentPageId = null;
            currentPage = null;
            docsViewer.innerHTML =
                '<div class="empty-state"><p>Select a page from the tree to view its content</p></div>';
        }
    } catch (error) {
        alert(
            `Failed to delete page: ${error instanceof Error ? error.message : "Unknown error"}`
        );
    } finally {
        deletePageConfirmBtn.removeAttribute("aria-busy");
        deletePageConfirmBtn.disabled = false;
    }
}

// Tags management
async function loadTags(): Promise<void> {
    if (!currentProjectId) return;

    try {
        projectTags = await apiRequest<Tag[]>(
            `/projects/${currentProjectId}/tags`
        );
    } catch (error) {
        console.error("Failed to load tags:", error);
        projectTags = [];
    }
}

function openTagsDialog(): void {
    renderTagsList();
    newTagNameInput.value = "";
    tagsDialog.showModal();
}

function closeTagsDialog(): void {
    tagsDialog.close();
}

function renderTagsList(): void {
    if (projectTags.length === 0) {
        tagsList.innerHTML = '<p class="muted">No tags yet</p>';
        return;
    }

    tagsList.innerHTML = projectTags
        .map(
            (tag) => `
            <div class="tag-item">
                <span>${escapeHtml(tag.name)}</span>
                <button type="button" class="outline delete-action delete-tag-btn" data-tag-id="${tag.id}">Delete</button>
            </div>
        `
        )
        .join("");

    tagsList.querySelectorAll(".delete-tag-btn").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
            const tagId = parseInt(
                (e.target as HTMLElement).dataset.tagId || "0",
                10
            );
            await deleteTag(tagId);
        });
    });
}

async function addTag(): Promise<void> {
    if (!currentProjectId) return;

    const name = newTagNameInput.value.trim();
    if (!name) return;

    addTagBtn.setAttribute("aria-busy", "true");
    addTagBtn.disabled = true;

    try {
        await apiRequest(`/projects/${currentProjectId}/tags`, {
            method: "POST",
            body: JSON.stringify({ name }),
        });
        newTagNameInput.value = "";
        await loadTags();
        renderTagsList();
    } catch (error) {
        alert(
            `Failed to create tag: ${error instanceof Error ? error.message : "Unknown error"}`
        );
    } finally {
        addTagBtn.removeAttribute("aria-busy");
        addTagBtn.disabled = false;
    }
}

async function deleteTag(tagId: number): Promise<void> {
    if (!currentProjectId) return;

    if (!confirm("Are you sure you want to delete this tag?")) return;

    try {
        await apiRequest(`/projects/${currentProjectId}/tags/${tagId}`, {
            method: "DELETE",
        });
        await loadTags();
        renderTagsList();
    } catch (error) {
        alert(
            `Failed to delete tag: ${error instanceof Error ? error.message : "Unknown error"}`
        );
    }
}

// Search by tags
let searchSelectedTagIds: number[] = [];

function openSearchDialog(): void {
    searchSelectedTagIds = [];
    searchResults.style.display = "none";
    searchResultsList.innerHTML = "";
    renderSearchTagsList();
    searchDialog.showModal();
}

function closeSearchDialog(): void {
    searchDialog.close();
    searchSelectedTagIds = [];
}

function renderSearchTagsList(): void {
    if (projectTags.length === 0) {
        searchTagsList.innerHTML = '<p class="muted">No tags available</p>';
        searchExecuteBtn.disabled = true;
        return;
    }

    searchTagsList.innerHTML = projectTags
        .map(
            (tag) => `
            <label>
                <input type="checkbox" value="${tag.id}" ${searchSelectedTagIds.includes(tag.id) ? "checked" : ""}>
                ${escapeHtml(tag.name)}
            </label>
        `
        )
        .join("");

    searchTagsList
        .querySelectorAll('input[type="checkbox"]')
        .forEach((checkbox) => {
            checkbox.addEventListener("change", (e) => {
                const input = e.target as HTMLInputElement;
                const tagId = parseInt(input.value, 10);
                if (input.checked) {
                    if (!searchSelectedTagIds.includes(tagId)) {
                        searchSelectedTagIds.push(tagId);
                    }
                } else {
                    searchSelectedTagIds = searchSelectedTagIds.filter(
                        (id) => id !== tagId
                    );
                }
                searchExecuteBtn.disabled = searchSelectedTagIds.length === 0;
            });
        });

    searchExecuteBtn.disabled = searchSelectedTagIds.length === 0;
}

async function executeSearch(): Promise<void> {
    if (!currentProjectId || searchSelectedTagIds.length === 0) return;

    searchExecuteBtn.setAttribute("aria-busy", "true");
    searchExecuteBtn.disabled = true;

    try {
        const params = searchSelectedTagIds
            .map((id) => `tag_ids=${id}`)
            .join("&");
        const pages = await apiRequest<DocumentationPage[]>(
            `/projects/${currentProjectId}/docs/search?${params}`
        );

        searchResults.style.display = "block";

        if (pages.length === 0) {
            searchResultsList.innerHTML =
                '<p class="muted">No pages found matching all selected tags</p>';
        } else {
            searchResultsList.innerHTML = pages
                .map(
                    (page) => `
                    <div class="search-result-item" data-page-id="${page.id}">
                        <strong>${escapeHtml(page.title)}</strong>
                        <div class="page-tags">
                            ${page.tags.map((t) => `<span class="tag">${escapeHtml(t.name)}</span>`).join("")}
                        </div>
                    </div>
                `
                )
                .join("");

            searchResultsList
                .querySelectorAll(".search-result-item")
                .forEach((item) => {
                    item.addEventListener("click", () => {
                        const pageId = parseInt(
                            (item as HTMLElement).dataset.pageId || "0",
                            10
                        );
                        closeSearchDialog();
                        selectPage(pageId);
                        // Expand parent nodes to show the selected page
                        expandToPage(pageId);
                    });
                });
        }
    } catch (error) {
        searchResultsList.innerHTML = `<p class="error">Search failed: ${error instanceof Error ? error.message : "Unknown error"}</p>`;
        searchResults.style.display = "block";
    } finally {
        searchExecuteBtn.removeAttribute("aria-busy");
        searchExecuteBtn.disabled = false;
    }
}

function expandToPage(pageId: number): void {
    // Find the path to this page and expand all parent nodes
    function findPath(
        nodes: DocumentationTreeNode[],
        targetId: number,
        path: number[]
    ): number[] | null {
        for (const node of nodes) {
            if (node.page.id === targetId) {
                return path;
            }
            const found = findPath(node.children, targetId, [
                ...path,
                node.page.id,
            ]);
            if (found) return found;
        }
        return null;
    }

    const path = findPath(docTree, pageId, []);
    if (path) {
        path.forEach((id) => expandedNodes.add(id));
        renderDocTree();
    }
}

initDocsPage();
